import { api, Upload } from './index.js';

/**
 * Create a new video project
 */
export const createVideoProject = async data => {
	const res = await api.post('/video-projects', data);
	return res.data;
};

/**
 * Upload video file to S3 using multipart upload
 */
export const uploadVideoToS3 = async ({ file, projectId, onProgress }) => {
	const upload = new Upload({
		fileRef: file,
		projectId: projectId,
		updateProgress: progress => onProgress?.(progress),
	});

	return await upload.start(); // Returns { uploadId, key }
};

/**
 * Update video upload metadata
 */
export const updateVideoUploadMetadata = async ({ projectUuid, videoUuid, uploadId, key }) => {
	const res = await api.patch(`/video-projects/${projectUuid}/videos/${videoUuid}`, {
		upload_id: uploadId,
		key,
	});

	return res.data;
};

/**
 * Start video processing
 */
export const startVideoProcessing = async ({ projectUuid, videoUuid, configuration }) => {
	const res = await api.post(
		`/video-projects/${projectUuid}/videos/${videoUuid}/detect`,
		configuration
	);
	return res.data;
};

/**
 * Get video project
 */
export const getVideoProject = async projectId => {
	const { data } = await api.get(`/video-projects/${projectId}`);
	return data;
};

/**
 * Get video project video details
 */
export const getVideoDetails = async (projectId, videoId) => {
	const { data } = await api.get(`/video-projects/${projectId}/videos/${videoId}`);
	return data;
};

/**
 * Get video project results
 */
export const getVideoResults = async (projectId, jobId) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}`);
	return data;
};

/**
 * Get video project analysis results
 */
export const getVideoAnalysisResults = async (projectId, jobId) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}/analysis`);
	return data;
};

/**
 * Update video project configuration
 */
export const updateVideoProjectConfiguration = async (projectId, configuration) => {
	const { data } = await api.post(`/video-projects/${projectId}/configuration`, configuration);
	return data;
};

/**
 * Export video results
 */
export const exportVideoResults = async (projectId, format) => {
	const { data } = await api.post(`/video-projects/${projectId}/export`, { format });
	return data;
};

/**
 * Reprocess video with new configuration
 */
export const reprocessVideo = async (projectId, configuration) => {
	const { data } = await api.post(`/video-projects/${projectId}/reprocess`, { configuration });
	return data;
};

/**
 * The video models the API accepts, each with the name the backend gives it: [{ model_id, label }]
 */
export const getVideoModels = async () => {
	const { data } = await api.get('/video-projects/models');
	const models = data?.data?.models;
	return Array.isArray(models) ? models : [];
};

/**
 * What a chosen video seems to be about, from a few of its frames, before it is uploaded:
 * { subject, scene, in_view, camera, mode, alternatives, confidence }. Throws when there is no suggestion.
 */
export const suggestCounting = async ({ frames, durationSeconds }) => {
	const res = await api.post('/video-projects/suggest', {
		frames,
		duration_seconds: durationSeconds || null,
	});
	return res.data?.data || null;
};

/**
 * The corrections layer of an analysis: people's edits over its tracks (remove, merge, add, not the subject)
 */
export const getCorrections = async (projectId, jobId) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}/corrections`);
	return data;
};

export const addCorrection = async (projectId, jobId, edit) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/corrections`,
		edit
	);
	return data;
};

export const undoCorrection = async (projectId, jobId, editId) => {
	const { data } = await api.delete(
		`/video-projects/${projectId}/results/${jobId}/corrections/${editId}`
	);
	return data;
};

/**
 * The assistant on an analysis (prices in credits, look again at a frame, find what was missed) and credits
 */
export const getAssistantPrice = async (projectId, jobId) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}/assistant/price`);
	return data;
};

export const assistantLookAgain = async (projectId, jobId, frame) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/assistant/look-again`,
		{ frame }
	);
	return data;
};

export const assistantHint = async (projectId, jobId, frame, text) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/assistant/hint`,
		{
			frame,
			text,
		}
	);
	return data;
};

export const acceptCorrection = async (projectId, jobId, editId) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/corrections/${editId}/accept`
	);
	return data;
};

export const retryCorrection = async (projectId, jobId, editId) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/corrections/${editId}/retry`
	);
	return data;
};

export const getUsualSizes = async (projectId, jobId) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}/usual-sizes`);
	return data;
};

export const assistantCheck = async (projectId, jobId, dryRun) => {
	const { data } = await api.post(
		`/video-projects/${projectId}/results/${jobId}/assistant/check`,
		{
			dry_run: !!dryRun,
		}
	);
	return data;
};

export const rerunWithExamples = async (projectId, jobId) => {
	const { data } = await api.post(`/video-projects/${projectId}/results/${jobId}/rerun`);
	return data;
};

/**
 * Checking an analysis track by track: the pictures (status `preparing` until they are cut), what was answered,
 * and the tracks that look like the ones said no to
 */
export const getReview = async (projectId, jobId, retry = false) => {
	const { data } = await api.get(`/video-projects/${projectId}/results/${jobId}/review`, {
		params: retry ? { retry: true } : undefined,
	});
	return data;
};

export const setReviewVerdict = async (projectId, jobId, trackId, verdict) => {
	const { data } = await api.put(
		`/video-projects/${projectId}/results/${jobId}/review/${trackId}`,
		{ verdict }
	);
	return data;
};

export const getReviewAlike = async (projectId, jobId, { no, yes }) => {
	const { data } = await api.post(`/video-projects/${projectId}/results/${jobId}/review/alike`, {
		no,
		yes,
	});
	return data;
};
